import { useState, useEffect } from "react"
import { useQueryState } from "nuqs"
import ApiError from "../../components/ApiError"
import {
  useMissingPathologyCounts,
  useDistinctBodyParts,
} from "../../hooks/useMissingPathologyLogs"
import MissingPathologyStatsTable from "./components/MissingPathologyStatsTable"

const MissingPathologies = () => {
  const [status, setStatus] = useState("UNMAPPED")
  const [bodyPart, setBodyPart] = useQueryState("bodyPart", {
    defaultValue: "",
  })
  const [search, setSearch] = useState("")
  const [sorting, setSorting] = useState([{ id: "logCount", desc: true }])
  const [dateFrom, setDateFrom] = useQueryState("from", { defaultValue: "" })
  const [dateTo, setDateTo] = useQueryState("to", { defaultValue: "" })

  const { data: distinctBodyParts } = useDistinctBodyParts()

  useEffect(() => {
    if (bodyPart) return
    const list = distinctBodyParts?.data || []
    if (list.length === 0) return
    const preferred = list.includes("spine") ? "spine" : list[0]
    setBodyPart(preferred)
  }, [distinctBodyParts, bodyPart])

  const countsFilters = { BodyPart: bodyPart, search }
  const {
    data: counts,
    isError,
    error,
  } = useMissingPathologyCounts({ filters: countsFilters })

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <>
      <div className="px-4 py-2 flex justify-between items-start mt-2 gap-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Missing Pathologies</h1>
          <div className="border-l border-gray-300 h-8 mx-2"></div>
          <div className="flex items-center gap-3 bg-white border border-gray-300 rounded-md px-3">
            <span className="text-sm font-medium text-gray-700">From:</span>
            <div className="relative">
              <input
                className="appearance-none px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="mm/dd/yyyy"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">To:</span>
            <div className="relative">
              <input
                className="appearance-none px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                max={new Date().toISOString().split("T")[0]}
                min={dateFrom || undefined}
                placeholder="mm/dd/yyyy"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                className="px-2 py-1 mr-1 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md transition-colors"
                onClick={() => {
                  setDateFrom("")
                  setDateTo("")
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
      <hr className="w-full my-4 border-gray-300" />
      <MissingPathologyStatsTable
        bodyPart={bodyPart}
        bodyPartOptions={distinctBodyParts?.data || []}
        counts={counts}
        dateFrom={dateFrom}
        dateTo={dateTo}
        search={search}
        setBodyPart={setBodyPart}
        setSearch={setSearch}
        setSorting={setSorting}
        setStatus={setStatus}
        sorting={sorting}
        status={status}
      />
    </>
  )
}

export default MissingPathologies
